'use client';

import {
  Dispatch,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import { useTranslations } from 'next-intl';
import { useQuickToast } from 'hooks/use-toast';
import {
  AddToCartApiCall,
  getPaymentMethods
} from 'services/parts-diagram/actions';
import { GA_PDP_DIAGRAM } from 'constants/analyticsConstants';
import {
  CATEGORY_TYPES,
  ComponentsType,
  LineItem,
  PartDetailsAndSchematicResponse,
  PartItem,
  AnalyticsCommonInformation
} from 'types/parts-diagram';
import { useAddToCartItemEvent } from 'hooks/analytics/use-add-to-cart-item';
import {
  getFormattedItemData,
  getFormattedItemSummary
} from 'components/product-comparison-grid/product-comparison-card/utils';
import { useSelectItemEvent } from 'hooks/analytics/use-select-item-event';

interface PartDiagramContext {
  isToggle: boolean;
  handleToggle: () => void;
  displayCategory: CATEGORY_TYPES;
  handlePartsContentCategory: (
    category: CATEGORY_TYPES,
    components?: ComponentsType | null,
    partsDetails?: PartDetailsAndSchematicResponse
  ) => void;
  componentsData: ComponentsType;
  partsDetails: PartDetailsAndSchematicResponse;
  selectedPart: PartItem;
  handleSelectedPart: (partItem: PartItem) => void;
  isCatCorp: boolean;
  setIsCatCorp: Dispatch<SetStateAction<boolean>>;
  handleAddToCart: (
    part: LineItem,
    quantity: string,
    analyticsInformation: AnalyticsCommonInformation
  ) => void;
  equipmentError: boolean;
  setEquipmentError: Dispatch<SetStateAction<boolean>>;
  setDisableAddToCart: Dispatch<SetStateAction<boolean>>;
  disableAddToCart: boolean;
  serverData;
  setServerData;
  clickedLineItem: LineItem;
  setClickedLineItem: Dispatch<SetStateAction<LineItem>>;
  analyticsCommonInfo?: AnalyticsCommonInformation;
  setAnalyticsCommonInfo?: Dispatch<SetStateAction<AnalyticsCommonInformation>>;
}

const PartDiagramContext = createContext<PartDiagramContext>(null);
export const usePartDiagram = () => useContext(PartDiagramContext);

export const PartDiagramContextProvider = ({ children }) => {
  const t = useTranslations();
  const toast = useQuickToast();
  const [isToggle, setIsToggle] = useState(false);
  const [displayCategory, setDisplayCategory] = useState<CATEGORY_TYPES>(
    CATEGORY_TYPES.LISTOFARRANGEMENTS
  );
  const [componentsData, setComponentsData] = useState<ComponentsType>();
  const [partsDetails, setPartsDetails] =
    useState<PartDetailsAndSchematicResponse>();
  const [selectedPart, setSelectedPart] = useState<PartItem | null>(null);
  const [isCatCorp, setIsCatCorp] = useState<boolean>(true);
  const [equipmentError, setEquipmentError] = useState<boolean>(false);
  const [disableAddToCart, setDisableAddToCart] = useState<boolean>(false);
  const [serverData, setServerData] = useState();

  const [analyticsCommonInfo, setAnalyticsCommonInfo] =
    useState<AnalyticsCommonInformation>({});
  const [clickedLineItem, setClickedLineItem] = useState<LineItem>();

  const handleToggle = useCallback(() => setIsToggle(!isToggle), [isToggle]);

  const { fireEvent: fireSelectItemEvent } = useSelectItemEvent();
  const { fireEvent: addToCartItemEvent } = useAddToCartItemEvent();

  const handleSelectedPart = useCallback(
    (partItem: PartItem) => setSelectedPart(partItem),
    []
  );

  const handlePartsContentCategory = (
    category: CATEGORY_TYPES,
    componentsData?: ComponentsType,
    partsDetails?: PartDetailsAndSchematicResponse
  ) => {
    setDisplayCategory(category);
    switch (category) {
      case CATEGORY_TYPES.PARTSCARDS:
        setComponentsData(componentsData);
        break;
      case CATEGORY_TYPES.PARTSDETAILS:
        setPartsDetails(partsDetails);
        break;
    }
  };

  async function handleAddToCart(part: LineItem, quantity: string) {
    setDisableAddToCart(true);
    const partItem = {
      ...part,
      quantity: quantity,
      sosCode: part?.priceAndAvailabilityData?.sosCode
    };
    // TODO - Input Validation - Ensure that all values passed to the service are allowed values:
    // part should be an object with the following properties:
    // partNumber should always be a string restricting generalRestrictedCharacters (@\constants\securityConstants.ts).
    // partName should always be a string restricting generalRestrictedCharacters (@\constants\securityConstants.ts).
    // quantity should always be a number.
    // sosCode should always be an alphanumeric string.
    const { error, response } = await AddToCartApiCall(partItem);
    if (error) {
      toast.error({
        message: t('ITEM_ADD_ERROR')
      });
    } else {
      toast.success({
        message: t('ITEM_ADDED_TO_CART')
      });

      const { orderId } = response;
      const { paymentMethods } = await getPaymentMethods(orderId);

      if (paymentMethods) {
        const priceAndAvailabilityFormatedData = {
          ...part.priceAndAvailabilityData,
          type: '',
          name: part.name,
          imageURL: '',
          index: parseInt(part?.referenceNumber)
        };
        fireSelectItemEvent({
          item: {
            orderItemId: orderId,
            partNumber: partItem.partNumber,
            quantity: priceAndAvailabilityFormatedData.quantity,
            availabilityMessage: priceAndAvailabilityFormatedData
              ? priceAndAvailabilityFormatedData.availability?.[0]
                  ?.availabilityMessage
              : '',
            totalPrice: priceAndAvailabilityFormatedData
              ? priceAndAvailabilityFormatedData.unformattedTotalPrice &&
                Number(priceAndAvailabilityFormatedData.unformattedTotalPrice)
              : 0
          },
          index: parseInt(part?.referenceNumber),
          partName: partItem && `${partItem?.partNumber}: ${partItem?.name}`,
          locationId: '',
          itemSummary: {
            categoryPath: priceAndAvailabilityFormatedData.categoryPath,
            manufacturer: ''
          },
          isSearchPageEvent: true,
          itemListId: GA_PDP_DIAGRAM,
          itemListName: GA_PDP_DIAGRAM
        });

        addToCartItemEvent({
          locationId: '',
          index: parseInt(part?.referenceNumber),
          itemSummary: getFormattedItemSummary(
            priceAndAvailabilityFormatedData
          ),
          itemData: getFormattedItemData(
            orderId,
            priceAndAvailabilityFormatedData
          ),
          itemListId: GA_PDP_DIAGRAM,
          itemListName: GA_PDP_DIAGRAM
        });
      }
    }
    setDisableAddToCart(false);
  }

  const contextValues = useMemo(() => {
    return {
      isToggle,
      handleToggle,
      displayCategory,
      handlePartsContentCategory,
      componentsData,
      partsDetails,
      selectedPart,
      handleSelectedPart,
      isCatCorp,
      setIsCatCorp,
      handleAddToCart,
      equipmentError,
      setEquipmentError,
      disableAddToCart,
      serverData,
      setServerData,
      analyticsCommonInfo,
      setAnalyticsCommonInfo,
      clickedLineItem,
      setClickedLineItem,
      setDisableAddToCart
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isToggle,
    handleToggle,
    displayCategory,
    componentsData,
    partsDetails,
    selectedPart,
    handleSelectedPart,
    isCatCorp,
    setIsCatCorp,
    equipmentError,
    setEquipmentError,
    disableAddToCart,
    serverData,
    setServerData,
    analyticsCommonInfo,
    setAnalyticsCommonInfo,
    clickedLineItem,
    setClickedLineItem,
    setDisableAddToCart
  ]);

  return (
    <PartDiagramContext.Provider value={contextValues}>
      {children}
    </PartDiagramContext.Provider>
  );
};
